import React, { useEffect, useState } from "react";
import TripTimeline from "./TripTimeLine";
import { useAuth } from '../AuthContext';
import { Navigate } from "react-router-dom";
import http from "../../Api/http";
import { useTranslation } from 'react-i18next';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Box,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { motion } from "framer-motion";

const TripsDashboard = () => {
    const [trips, setTrips] = useState([]);
    const { user, loadingUser } = useAuth();
    const [loading, setLoading] = useState(false);
    const { t, i18n } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    useEffect(() => {
        if (!user) return;

        async function fetchData() {
            setLoading(true);
            const lang = i18n.language || 'ar';
            try {
                const res = await http.getJsonAxios(http.actions.Trips.GetTrips, { lang }, t);
                if (res.status === 200) setTrips(res.data || []);
                else alert("Error: " + res.message);
            } catch (err) {
                console.error("Fetch error:", err);
                alert("Failed to load trips.");
            } finally {
                setLoading(false);
            }
        }

        fetchData();
        const interval = setInterval(fetchData, 30000);
        return () => clearInterval(interval);
    }, [i18n.language, user]);

    if (loadingUser) return null;
    if (!user || user.Role !== 1) return <Navigate to="/" />;

    // Group trips by DriverId and TripNo
    const groupedTrips = trips.reduce((acc, item) => {
        const key = `${item.DriverId}-${item.TripNo}`;
        if (!acc[key]) acc[key] = [];
        acc[key].push(item);
        return acc;
    }, {});

    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    const getTripColor = (wait) => {
        if (wait < 30) return 'green';
        if (wait < 60) return 'orange';
        return 'red';
    };

    return (
        <div className="min-h-screen pt-8 pb-20 bg-background" dir={t('dir')}>
            {/* عنوان About */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center mb-16"
            >
                <h1 className="text-4xl md:text-5xl font-bold mb-6 text-slate-900 dark:text-white">
                    {t('trips')}
                </h1>
                <div className="h-1.5 w-24 bg-accent mx-auto rounded-full" />
                {t('tripsCount')} {Object.keys(groupedTrips).length}
            </motion.div>

            {Object.entries(groupedTrips).map(([key, tripData], index) => {
                const trip = tripData[0]; // Use first entry for summary
                return (
                    <Accordion
                        key={index}
                        expanded={expanded === key}
                        onChange={handleAccordionChange(key)}
                        sx={{
                            mb: 2,
                            borderRadius: 2,
                            boxShadow: 2,
                            bgcolor: 'background.paper',
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls={`panel-${index}-content`}
                            id={`panel-${index}-header`}
                            sx={{
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Box sx={{ width: '100%', display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between' }}>
                                <Typography sx={{ fontWeight: 500 }}>
                                    <strong>{t('driverName')}: {trip.DriverName}</strong>
                                </Typography>
                                <Typography sx={{ fontWeight: 500 }}>
                                    <strong>{t('tripNo')}: {trip.TripNo}</strong>
                                </Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <TripTimeline trip={tripData} />
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </div>
    );
};

export default TripsDashboard;